import { UserAvatar } from "@promptlayer/ui";
import { ChevronsUpDown } from "lucide-react";
import { useState } from "react";

import { useUserSectionData } from "./hooks/useUserSectionData";
import { useWorkspaceNavigation } from "./hooks/useWorkspaceNavigation";
import Notifications from "./Notifications";
import { UserDropdownContent } from "./UserDropdownContent";

import AddWorkspaceMemberModal from "@/components/AddWorkspaceMemberModal";
import { ReferAFriendDialog } from "@/components/ReferAFriendDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { useAuth } from "@/context/auth-context";

interface UserSectionProps {
  isCollapsed: boolean;
}

const UserSection = ({ isCollapsed }: UserSectionProps) => {
  const auth = useAuth();

  const [open, setOpen] = useState(false);
  const [isReferralDialogOpen, setIsReferralDialogOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  // Custom hooks for data and navigation
  const {
    user,
    activeOrganization,
    activeWorkspace,
    organizations,
    workspaceInvites,
    userRole,
    canInvite,
    defaultOrganizationId,
    workspacesQuery,
    getDefaultWorkspaceId,
  } = useUserSectionData();

  const {
    handleOrganizationSelect,
    handleWorkspaceSelect,
    handleManageOrganizations,
    handleManageWorkspaces,
  } = useWorkspaceNavigation();

  // Wrapper functions that close the dropdown
  const closeMenu = () => setOpen(false);

  const handleWorkspaceSelectWithClose = (workspaceId: number) => {
    handleWorkspaceSelect(workspaceId, closeMenu);
  };

  const handleOrganizationSelectWithClose = (organizationId: number) => {
    handleOrganizationSelect(organizationId, closeMenu);
  };

  const handleManageOrganizationsWithClose = () => {
    handleManageOrganizations(closeMenu);
  };

  const handleManageWorkspacesWithClose = () => {
    handleManageWorkspaces(activeOrganization?.id, closeMenu);
  };

  const handleInviteClick = () => {
    setInviteModalOpen(true);
    closeMenu();
  };

  const handleReferFriendClick = () => {
    setIsReferralDialogOpen(true);
    closeMenu();
  };

  const handleLogout = () => {
    auth?.logout();
  };

  // Collapsed state - icon only
  if (isCollapsed) {
    return (
      <>
        <div className="flex flex-col items-center gap-1 px-2 py-3">
          <DropdownMenu open={open} onOpenChange={setOpen}>
            <BasicTooltip
              content={
                <div className="text-center">
                  <div className="font-medium">{user?.email}</div>
                  <div className="text-xs opacity-70">
                    {activeOrganization?.name} · {activeWorkspace?.name}
                  </div>
                </div>
              }
              side="right"
            >
              <DropdownMenuTrigger asChild>
                <button className="group relative flex h-9 w-9 items-center justify-center rounded-lg outline-none transition-all hover:bg-gray-100/80">
                  <UserAvatar email={user?.email || ""} className="h-6 w-6" />
                  <div className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded bg-gray-100 text-[8px] font-semibold text-gray-600 ring-2 ring-white">
                    {activeOrganization?.name?.charAt(0).toUpperCase() || "W"}
                  </div>
                </button>
              </DropdownMenuTrigger>
            </BasicTooltip>

            <DropdownMenuContent
              side="right"
              align="end"
              sideOffset={12}
              className="w-72 overflow-hidden rounded-xl border border-gray-200/80 bg-white p-0 shadow-xl"
            >
              <UserDropdownContent
                email={user?.email || ""}
                userRole={userRole}
                workspaceInvites={workspaceInvites}
                organizations={organizations}
                workspaces={workspacesQuery.data?.workspaces || []}
                activeOrganizationId={activeOrganization?.id}
                activeWorkspaceId={activeWorkspace?.id}
                defaultOrganizationId={defaultOrganizationId}
                canInvite={canInvite}
                onWorkspaceSelect={handleWorkspaceSelectWithClose}
                onOrganizationSelect={handleOrganizationSelectWithClose}
                onInviteClick={handleInviteClick}
                onManageOrganizations={handleManageOrganizationsWithClose}
                onManageWorkspaces={handleManageWorkspacesWithClose}
                onReferFriend={handleReferFriendClick}
                onLogout={handleLogout}
                onCloseMenu={closeMenu}
                getDefaultWorkspaceId={getDefaultWorkspaceId}
              />
            </DropdownMenuContent>
          </DropdownMenu>

          <Notifications />
        </div>

        <AddWorkspaceMemberModal isOpen={inviteModalOpen} setOpen={setInviteModalOpen} />
        <ReferAFriendDialog setIsOpen={setIsReferralDialogOpen} isOpen={isReferralDialogOpen} />
      </>
    );
  }

  // Expanded state - full view with Notion-style design
  return (
    <>
      <div className="flex items-center gap-1.5 px-2 py-2">
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <button className="group flex min-w-0 flex-1 items-center gap-2.5 rounded-lg px-2 py-1.5 outline-none transition-all hover:bg-gray-100/80 focus-visible:bg-gray-100/80">
              <div className="relative flex-shrink-0">
                <UserAvatar email={user?.email || ""} className="h-7 w-7" />
                <div className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded bg-gray-100 text-[8px] font-bold text-gray-600 ring-2 ring-white">
                  {activeOrganization?.name?.charAt(0).toUpperCase() || "W"}
                </div>
              </div>
              <div className="min-w-0 flex-1 text-left">
                <div className="flex items-center gap-1">
                  <span className="truncate text-sm font-medium text-gray-900">
                    {activeWorkspace?.name || "Select workspace"}
                  </span>
                </div>
                <span className="block truncate text-xs text-gray-500">
                  {activeOrganization?.name || "No organization"}
                </span>
              </div>
              <ChevronsUpDown className="size-3.5 flex-shrink-0 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100" />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            side="top"
            align="start"
            sideOffset={8}
            className="w-72 overflow-hidden rounded-xl border border-gray-200/80 bg-white p-0 shadow-xl"
          >
            <UserDropdownContent
              email={user?.email || ""}
              userRole={userRole}
              workspaceInvites={workspaceInvites}
              organizations={organizations}
              workspaces={workspacesQuery.data?.workspaces || []}
              activeOrganizationId={activeOrganization?.id}
              activeWorkspaceId={activeWorkspace?.id}
              defaultOrganizationId={defaultOrganizationId}
              canInvite={canInvite}
              onWorkspaceSelect={handleWorkspaceSelectWithClose}
              onOrganizationSelect={handleOrganizationSelectWithClose}
              onInviteClick={handleInviteClick}
              onManageOrganizations={handleManageOrganizationsWithClose}
              onManageWorkspaces={handleManageWorkspacesWithClose}
              onReferFriend={handleReferFriendClick}
              onLogout={handleLogout}
              onCloseMenu={closeMenu}
              getDefaultWorkspaceId={getDefaultWorkspaceId}
            />
          </DropdownMenuContent>
        </DropdownMenu>

        <Notifications />
      </div>

      <AddWorkspaceMemberModal isOpen={inviteModalOpen} setOpen={setInviteModalOpen} />
      <ReferAFriendDialog setIsOpen={setIsReferralDialogOpen} isOpen={isReferralDialogOpen} />
    </>
  );
};

export default UserSection;
